import { ReactNode } from "react";

import {
  ArrowDownIcon,
  ArrowLeftRightIcon,
  ArrowUpIcon,
  BanIcon,
  CircleDotIcon,
  GoalIcon,
  MonitorPlayIcon,
  RectangleVerticalIcon,
} from "lucide-react";

import type { IMatchEvent } from "@/types/match-details";

interface EventMeta {
  label: string;
  icon: ReactNode;
}

const ICON = "size-4 shrink-0";

const EVENT_META: Record<string, EventMeta> = {
  Goal: {
    label: "Goal",
    icon: <GoalIcon className={`${ICON} text-emerald-600`} />,
  },
  PenaltyGoal: {
    label: "Penalty",
    icon: <GoalIcon className={`${ICON} text-emerald-600`} />,
  },
  OwnGoal: {
    label: "Own goal",
    icon: <GoalIcon className={`${ICON} text-red-600`} />,
  },
  MissedPenalty: {
    label: "Missed penalty",
    icon: <BanIcon className={`${ICON} text-red-600`} />,
  },
  YellowCard: {
    label: "Yellow card",
    icon: (
      <RectangleVerticalIcon
        className={`${ICON} fill-yellow-400 text-yellow-500`}
      />
    ),
  },
  SecondYellow: {
    label: "Second yellow",
    icon: (
      <RectangleVerticalIcon
        className={`${ICON} fill-orange-400 text-orange-500`}
      />
    ),
  },
  RedCard: {
    label: "Red card",
    icon: (
      <RectangleVerticalIcon className={`${ICON} fill-red-500 text-red-600`} />
    ),
  },
  Substitution: {
    label: "Substitution",
    icon: <ArrowLeftRightIcon className={`${ICON} text-muted-foreground`} />,
  },
  VAR: {
    label: "VAR",
    icon: <MonitorPlayIcon className={`${ICON} text-muted-foreground`} />,
  },
};

/** A type this build does not know still shows, under the source's own name. */
const metaFor = (type?: string | null): EventMeta =>
  (type ? EVENT_META[type] : undefined) ?? {
    label: type ?? "Event",
    icon: <CircleDotIcon className={`${ICON} text-muted-foreground`} />,
  };

function EventText({ event }: { event: IMatchEvent }) {
  if (event.type === "Substitution") {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-3">
        {event.player?.name && (
          <span className="inline-flex items-center gap-1">
            <ArrowUpIcon className="size-3.5 text-emerald-600" />
            {event.player.name}
          </span>
        )}
        {event.playerOut?.name && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <ArrowDownIcon className="size-3.5 text-red-600" />
            {event.playerOut.name}
          </span>
        )}
      </span>
    );
  }
  return (
    <span>
      {event.player?.name}
      {event.assist?.name && (
        <span className="text-muted-foreground">
          {" "}
          (assist {event.assist.name})
        </span>
      )}
      {event.detail && (
        <span className="text-muted-foreground"> {event.detail}</span>
      )}
    </span>
  );
}

/**
 * The match's events in match order: home-team events on the left, away on the right,
 * the minute in the middle. A VAR or card event can name nobody, so the player is optional.
 */
export function MatchEventsTimeline({
  events,
  homeTeamId,
}: {
  events: IMatchEvent[];
  homeTeamId?: string | null;
}) {
  return (
    <ol className="flex flex-col divide-y">
      {events.map((event, i) => {
        const { label, icon } = metaFor(event.type);
        const content = (
          <span className="inline-flex items-center gap-2">
            {icon}
            <span className="text-xs text-muted-foreground">{label}</span>
            <EventText event={event} />
          </span>
        );
        const isHome = !!homeTeamId && event.teamId === homeTeamId;
        return (
          <li
            key={i}
            className="grid grid-cols-[1fr_3rem_1fr] items-center gap-2 py-1.5 text-sm"
          >
            <div className="flex justify-end text-right">
              {isHome && content}
            </div>
            <span className="text-center font-mono text-xs text-muted-foreground">
              {event.minute ? `${event.minute}'` : "—"}
            </span>
            <div>{!isHome && content}</div>
          </li>
        );
      })}
    </ol>
  );
}
